import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { headerBranchId } from '../food-delivery/helpers';
import { branchService } from '@/data/branchService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  ClaimReasonTables,
  EXTERNAL_CLAIM_REFUND_TYPE_LABELS,
  EXTERNAL_CLAIM_STATUS_COLOR,
  EXTERNAL_CLAIM_STATUS_LABELS,
  ExternalClaim,
  ExternalClaimStatus,
  FOOD_PROVIDER_LABELS,
  FoodIntegration
} from '@/types/FoodDelivery.interface';
import { Branch } from '@/types/Branch.interface';
import FoodDeliveryEmptyState from '../food-delivery/components/FoodDeliveryEmptyState';

type Toast = { msg: string; severity: 'success' | 'error' | 'info' } | null;

const fmt = (v: string | null) =>
  v ? new Date(v).toLocaleString('tr-TR') : '—';

/**
 * Kalan süre rozeti. İade 4 saat içinde yanıtlanmazsa platform iadeyi satıcı
 * aleyhine sonuçlandırır — bu yüzden süre metin olarak değil, renkli ve
 * ilk bakışta okunur biçimde gösterilir.
 */
const RemainingChip: React.FC<{ remainingMs: number | null }> = ({
  remainingMs
}) => {
  if (remainingMs === null) return <span>—</span>;
  if (remainingMs <= 0) {
    return <Chip size="small" color="error" label="Süre doldu" />;
  }
  const totalMinutes = Math.floor(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const label = hours > 0 ? `${hours} sa ${minutes} dk` : `${minutes} dk`;
  return (
    <Chip
      size="small"
      color={totalMinutes <= 60 ? 'warning' : 'default'}
      variant={totalMinutes <= 60 ? 'filled' : 'outlined'}
      label={label}
    />
  );
};

/**
 * Müşteri iadeleri ekranı (plan Faz 5).
 *
 * Sıralama backend'de "süresi en yakın olan üstte" şeklindedir; operatör ilk
 * neye bakacağını aramak zorunda kalmaz.
 */
const FoodDeliveryClaims: React.FC = () => {
  const { isBranchAdmin, isAdminView, currentBranch } = useUserViewMode();

  const [branches, setBranches] = useState<Branch[]>([]);
  const headerBranch = headerBranchId(currentBranch, isAdminView);
  const [branchId, setBranchId] = useState<number | null>(headerBranch);
  // Üst menüden şube değişince sayfa da o şubeye geçer.
  useEffect(() => {
    if (headerBranch) setBranchId(headerBranch);
  }, [headerBranch]);
  const [integrations, setIntegrations] = useState<FoodIntegration[]>([]);
  const [rows, setRows] = useState<ExternalClaim[]>([]);
  const [reasons, setReasons] = useState<ClaimReasonTables | null>(null);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  // Ret modalı (window.confirm/prompt KULLANILMAZ).
  const [rejectTarget, setRejectTarget] = useState<ExternalClaim | null>(null);
  const [rejectReasonId, setRejectReasonId] = useState<number | ''>('');
  const [rejectNote, setRejectNote] = useState('');

  useEffect(() => {
    if (isBranchAdmin) return;
    branchService
      .getAllFlat({ getAll: true })
      .then(setBranches)
      .catch(() =>
        setToast({ msg: 'Şubeler getirilemedi.', severity: 'error' })
      );
  }, [isBranchAdmin]);

  useEffect(() => {
    foodDeliveryService
      .getClaimReasons()
      .then(setReasons)
      .catch(() =>
        setToast({ msg: 'İade nedenleri getirilemedi.', severity: 'error' })
      );
  }, []);

  useEffect(() => {
    if (!branchId) {
      setIntegrations([]);
      return;
    }
    foodDeliveryService
      .listByBranch(branchId)
      .then(setIntegrations)
      .catch(() =>
        setToast({ msg: 'Entegrasyonlar getirilemedi.', severity: 'error' })
      );
  }, [branchId]);

  const load = useCallback(async () => {
    if (!branchId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      setRows(await foodDeliveryService.listClaims({ branchId, onlyOpen }));
    } catch {
      setToast({ msg: 'İadeler getirilemedi.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [branchId, onlyOpen]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    if (integrations.length === 0) return;
    setLoading(true);
    try {
      let created = 0;
      for (const integration of integrations) {
        const res = await foodDeliveryService.refreshClaims(integration.id);
        created += res.created;
      }
      setToast({
        msg: created
          ? `${created} yeni iade alındı.`
          : 'Yeni iade yok; liste güncel.',
        severity: created ? 'success' : 'info'
      });
      await load();
    } catch {
      setToast({ msg: 'İadeler yenilenemedi.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (row: ExternalClaim) => {
    setBusyId(row.id);
    try {
      await foodDeliveryService.acceptClaim(row.id);
      setToast({ msg: 'İade kabul edildi.', severity: 'success' });
      await load();
    } catch (err) {
      setToast({
        msg: `İade kabul edilemedi: ${
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? 'bilinmeyen hata'
        }`,
        severity: 'error'
      });
    } finally {
      setBusyId(null);
    }
  };

  const openReject = (row: ExternalClaim) => {
    setRejectReasonId(reasons?.unresolveReasons[0]?.id ?? '');
    setRejectNote('');
    setRejectTarget(row);
  };

  const confirmReject = async () => {
    if (!rejectTarget || rejectReasonId === '') return;
    const row = rejectTarget;
    setRejectTarget(null);
    setBusyId(row.id);
    try {
      await foodDeliveryService.unresolveClaim(
        row.id,
        Number(rejectReasonId),
        rejectNote || undefined
      );
      setToast({
        msg: 'İade reddedildi; platform inceleyecek.',
        severity: 'success'
      });
      await load();
    } catch (err) {
      setToast({
        msg: `İade reddedilemedi: ${
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? 'bilinmeyen hata'
        }`,
        severity: 'error'
      });
    } finally {
      setBusyId(null);
    }
  };

  const isOpen = (row: ExternalClaim) =>
    row.status === ExternalClaimStatus.WAITING_IN_ACTION ||
    row.status === ExternalClaimStatus.WAITING_FOR_SELLER_REFUND;

  const overdueCount = useMemo(
    () => rows.filter((r) => isOpen(r) && (r.remainingMs ?? 1) <= 0).length,
    [rows]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography variant="h3" component="h3" gutterBottom>
              Müşteri İadeleri
            </Typography>
            <Typography variant="subtitle2">
              Platformda açılan iadeleri kabul edin ya da reddedin. Trendyol
              GO’da iadeye <strong>4 saat</strong> içinde yanıt verilmelidir;
              süre dolarsa iade satıcı aleyhine sonuçlanabilir.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={!branchId || loading || integrations.length === 0}
          >
            Platformdan Yenile
          </Button>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
              sx={{ mb: 3 }}
            >
              {!isBranchAdmin && (
                <Autocomplete
                  sx={{ minWidth: 300 }}
                  options={branches}
                  getOptionLabel={(b) => b.name}
                  value={branches.find((b) => b.id === branchId) ?? null}
                  onChange={(_e, val) => setBranchId(val?.id ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Şube seç" />
                  )}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyOpen}
                    onChange={(e) => setOnlyOpen(e.target.checked)}
                  />
                }
                label="Yalnız yanıt bekleyenler"
              />
            </Stack>

            {overdueCount > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {overdueCount} iadede yanıt süresi doldu. Platform bu iadeleri
                satıcı aleyhine sonuçlandırabilir.
              </Alert>
            )}

            {!branchId ? (
              <Alert severity="info">
                İadeleri görüntülemek için bir şube seçin.
              </Alert>
            ) : loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : integrations.length === 0 ? (
              <FoodDeliveryEmptyState
                title="Entegrasyon yok"
                message="Bu şubede yemek platformu entegrasyonu yok; iade de gelmez. Önce bir entegrasyon oluşturun."
              />
            ) : rows.length === 0 ? (
              <Alert severity="success">
                {onlyOpen
                  ? 'Yanıt bekleyen iade yok.'
                  : 'Bu şubede hiç iade kaydı yok.'}
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Platform</TableCell>
                      <TableCell>Sipariş No</TableCell>
                      <TableCell>Neden</TableCell>
                      <TableCell>İade Türü</TableCell>
                      <TableCell align="right">Tutar</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>Kalan Süre</TableCell>
                      <TableCell align="right">İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          {FOOD_PROVIDER_LABELS[row.providerCode]}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.orderNumber ?? '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fmt(row.claimCreatedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          {(row.items ?? []).map((item) => (
                            <Box key={item.id} sx={{ mb: 0.5 }}>
                              <Typography variant="body2">
                                {item.reasonName ?? 'Belirtilmemiş'}
                              </Typography>
                              {item.note && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ whiteSpace: 'pre-wrap' }}
                                >
                                  “{item.note}”
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              EXTERNAL_CLAIM_REFUND_TYPE_LABELS[row.refundType]
                            }
                          />
                          {row.type && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {row.type === 'Standard'
                                ? 'Tam iade'
                                : 'Kısmi iade'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row.totalPrice
                            ? `${Number(row.totalPrice).toFixed(2)} ₺`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={EXTERNAL_CLAIM_STATUS_COLOR[row.status]}
                            label={EXTERNAL_CLAIM_STATUS_LABELS[row.status]}
                          />
                          {row.unresolveReasonId && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Ret nedeni: {row.unresolveReasonId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isOpen(row) ? (
                            <RemainingChip remainingMs={row.remainingMs} />
                          ) : (
                            <Typography variant="caption">
                              {fmt(row.completedAt)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isOpen(row) ? (
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              <Tooltip title="İadeyi kabul et">
                                <span>
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    startIcon={
                                      <CheckCircleIcon fontSize="small" />
                                    }
                                    disabled={busyId === row.id}
                                    onClick={() => handleAccept(row)}
                                  >
                                    Kabul
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="İadeyi reddet (platform inceler)">
                                <span>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    startIcon={<CancelIcon fontSize="small" />}
                                    disabled={busyId === row.id || !reasons}
                                    onClick={() => openReject(row)}
                                  >
                                    Reddet
                                  </Button>
                                </span>
                              </Tooltip>
                            </Stack>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              İşlem tamamlandı
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>İadeyi reddet</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Sipariş {rejectTarget?.orderNumber ?? '—'} için iade reddedilecek.
            Platform itirazınızı inceleyip nihai kararı verir. Ret nedeni,
            platformun tanımlı listesinden seçilmelidir.
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="Ret nedeni"
            value={rejectReasonId}
            onChange={(e) => setRejectReasonId(Number(e.target.value))}
            sx={{ mb: 2 }}
          >
            {(reasons?.unresolveReasons ?? []).map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Not (opsiyonel)"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Vazgeç</Button>
          <Button
            color="error"
            variant="contained"
            disabled={rejectReasonId === ''}
            onClick={confirmReject}
          >
            Reddet
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliveryClaims;
